import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navigation from '../components/Navigation';
import UserCard from '../components/UserCard';
import RegisterForm from '../components/RegisterForm';
import { usersApi } from '../utils/api';
import type { User } from '../types/user';

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeleteUser = async (userId: number) => {
    try {
      await usersApi.delete(userId);
      await fetchUsers();
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleDisableUser = async (userId: number) => {
    try {
      await usersApi.disable(userId);
      await fetchUsers();
    } catch (err) {
      setError('Failed to disable user');
    }
  };

  const handleEnableUser = async (userId: number) => {
    try {
      await usersApi.enable(userId);
      await fetchUsers();
    } catch (err) {
      setError('Failed to enable user');
    }
  };

  const handleResetPassword = async (userId: number, newPassword: string) => {
    try {
      await usersApi.resetPassword(userId, newPassword);
      await fetchUsers();
    } catch (err) {
      setError('Failed to reset password');
    }
  };

  const handleUserCreated = async () => {
    setShowCreateForm(false);
    await fetchUsers();
  };

  return (
    <>
      <Helmet>
        <title>Users - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        <div className="mb-8 flex justify-between items-center fade-in">
          <div>
            <h1 className="text-3xl font-light mb-2">
              User <span className="font-medium">Management</span>
            </h1>
            <p className="text-gray-600">Manage system users and permissions</p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-6 py-3 btn-primary rounded-md text-sm font-medium"
          >
            {showCreateForm ? 'Cancel' : 'Create User'}
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Create User Form */}
        {showCreateForm && (
          <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6 fade-in">
            <h2 className="text-lg font-medium mb-4">Create New User</h2>
            <RegisterForm onSuccess={handleUserCreated} />
          </div>
        )}

        {/* User Stats */}
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4 fade-in stagger-1">
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-light">{users.length}</div>
            <div className="text-xs text-gray-500">Total Users</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-light">
              {users.filter(u => u.is_active).length}
            </div>
            <div className="text-xs text-gray-500">Active</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-light">
              {users.filter(u => u.is_admin).length}
            </div>
            <div className="text-xs text-gray-500">Admins</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-light">
              {users.filter(u => !u.is_active).length}
            </div>
            <div className="text-xs text-gray-500">Inactive</div>
          </div>
        </div>

        {/* User List */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="flex space-x-2">
              <div className="pulse-dot"></div>
              <div className="pulse-dot"></div>
              <div className="pulse-dot"></div>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 fade-in stagger-2">
            {users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onDelete={handleDeleteUser}
                onDisable={handleDisableUser}
                onEnable={handleEnableUser}
                onResetPassword={handleResetPassword}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
